import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Modal, TextField, Typography } from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import { ptBR } from "date-fns/locale";
import React, { useCallback, useEffect, useState } from "react";
import { FiRotateCcw, FiTrash2 } from "react-icons/fi";

import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import { TaskInterface } from "../../common/interfaces";
import { useAuth } from "../../hooks/useAuth";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import ConfirmDialog from "../ConfirmDialog";
import { TaskStatusLabel } from "../enum/taskStatusEnum";
import PaginationControls from "../PaginationControls";
import PageToaster from "../toaster/PageToaster";
import { EmptyList, RowButton, TaskRow, TaskRowList } from "./TaskListModal.styled";

interface TaskOverdueModalProps {
  open: boolean;
  onClose: () => void;
  /** Avisa a página para atualizar quadro e contador do sino. */
  onChanged?: () => void;
  /** Só tem efeito para ADMIN: mesma empresa escolhida no quadro. */
  companyId?: number;
}

const PAGE_SIZE = 8;

/**
 * Tarefas que venceram sem serem concluídas. Aqui elas só podem ser reabertas
 * com uma nova data limite (voltam para o quadro em "A Fazer") ou canceladas,
 * o que apaga a tarefa de vez.
 */
const TaskOverdueModal: React.FC<TaskOverdueModalProps> = ({
  open,
  onClose,
  onChanged,
  companyId
}) => {
  const [tasks, setTasks] = useState<TaskInterface[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [search, setSearch] = useState("");

  // Linha com o seletor de nova data aberto.
  const [reopeningId, setReopeningId] = useState<number | null>(null);
  const [newDueDate, setNewDueDate] = useState<Date | null>(null);

  // Confirmação de cancelamento (exclusão definitiva).
  const [taskToCancel, setTaskToCancel] = useState<TaskInterface | null>(null);

  const { isAdmin, isManager, isOwner } = useAuth();
  /** Só quem valida trabalho alheio reabre uma tarefa vencida. */
  const canReopen = isAdmin || isManager || isOwner;

  const containerId = "toaster-task-overdue-modal";
  const { autoSuccess, autoError, autoWarn } = usePageToast(containerId, true);

  const handleHttpError = makeToasterErrorHandler(autoError);

  const fetchTasks = useCallback(
    async (currentPage: number, currentSearch: string) => {
      setLoading(true);
      try {
        const response = await api.get(`/task`, {
          params: {
            page: currentPage,
            limit: PAGE_SIZE,
            overdue: "true",
            search: currentSearch.trim() || undefined,
            company_id: companyId
          }
        });

        setTasks(response.data.items);
        setTotalPages(response.data.meta.totalPages || 1);
      } catch (error) {
        console.error("Erro ao buscar tarefas não realizadas:", error);
        handleHttpError(error);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyId]
  );

  useEffect(() => {
    if (!open) return;
    fetchTasks(page, search);
  }, [open, page, search, fetchTasks]);

  useEffect(() => {
    if (open) {
      setPage(1);
      setSearch("");
      setReopeningId(null);
      setNewDueDate(null);
    }
  }, [open]);

  const handleStartReopen = (task: TaskInterface) => {
    setReopeningId(task.task_id);
    setNewDueDate(null);
  };

  const handleConfirmReopen = async (task: TaskInterface) => {
    if (!newDueDate || isNaN(newDueDate.getTime())) {
      autoError("Selecione a nova data limite.");
      return;
    }

    if (newDueDate.getTime() <= Date.now()) {
      autoError("A nova data limite deve ser maior que a data atual.");
      return;
    }

    setBusyId(task.task_id);
    try {
      await api.patch(`/task/${task.task_id}/reopen`, {
        task_due_date: newDueDate.toISOString()
      });
      autoSuccess("Tarefa reaberta e devolvida ao quadro!");
      setReopeningId(null);
      setNewDueDate(null);
      await fetchTasks(page, search);
      onChanged?.();
    } catch (error) {
      console.error("Erro ao reabrir tarefa:", error);
      handleHttpError(error);
    } finally {
      setBusyId(null);
    }
  };

  const handleConfirmCancel = async () => {
    if (!taskToCancel) return;

    setBusyId(taskToCancel.task_id);
    try {
      await api.delete(`/task/${taskToCancel.task_id}`);
      autoWarn("Tarefa cancelada e removida definitivamente.");
      setTaskToCancel(null);
      await fetchTasks(page, search);
      onChanged?.();
    } catch (error) {
      console.error("Erro ao cancelar tarefa:", error);
      handleHttpError(error);
    } finally {
      setBusyId(null);
    }
  };

  const formatDate = (value: string | null) =>
    value
      ? new Date(value).toLocaleString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit"
        })
      : "—";

  return (
    <>
      <PageToaster containerId={containerId} />
      <Modal open={open} onClose={onClose} aria-labelledby="modal-task-overdue">
        <Box
          sx={{
            position: "absolute" as const,
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "1px solid var(--border-strong)",
            p: 4,
            borderRadius: 2,
            boxShadow: 24,
            width: { xs: "92vw", sm: "680px" },
            maxHeight: "90vh",
            overflowY: "auto"
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Tarefas Não Realizadas</Typography>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <TextField
            size="small"
            fullWidth
            label="Buscar por nome"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            sx={{ mb: 2 }}
          />

          <TaskRowList>
            {tasks.map((task) => (
              <TaskRow key={task.task_id} $accent="#fb7185">
                <h3>{task.task_title}</h3>
                {task.task_description && <p>{task.task_description}</p>}
                <div className="meta">
                  <span>Venceu em: {formatDate(task.task_due_date)}</span>
                  <span>Parou em: {TaskStatusLabel[task.task_status]}</span>
                </div>

                {reopeningId === task.task_id ? (
                  <div className="row-actions">
                    <LocalizationProvider
                      dateAdapter={AdapterDateFns}
                      adapterLocale={ptBR}
                    >
                      <DateTimePicker
                        label="Nova data limite"
                        value={newDueDate}
                        onChange={setNewDueDate}
                        format="dd/MM/yyyy HH:mm"
                        disablePast
                        viewRenderers={{
                          hours: renderTimeViewClock,
                          minutes: renderTimeViewClock,
                          seconds: renderTimeViewClock
                        }}
                        slotProps={{
                          textField: { size: "small" }
                        }}
                      />
                    </LocalizationProvider>

                    <RowButton
                      onClick={() => handleConfirmReopen(task)}
                      disabled={busyId === task.task_id}
                    >
                      Confirmar
                    </RowButton>
                    <RowButton
                      $danger
                      onClick={() => {
                        setReopeningId(null);
                        setNewDueDate(null);
                      }}
                    >
                      Voltar
                    </RowButton>
                  </div>
                ) : (
                  <div className="row-actions">
                    {canReopen && (
                      <RowButton
                        onClick={() => handleStartReopen(task)}
                        disabled={busyId === task.task_id}
                      >
                        <FiRotateCcw /> Reabrir com nova data
                      </RowButton>
                    )}
                    <RowButton
                      $danger
                      onClick={() => setTaskToCancel(task)}
                      disabled={busyId === task.task_id}
                    >
                      <FiTrash2 /> Cancelar tarefa
                    </RowButton>
                  </div>
                )}
              </TaskRow>
            ))}

            {!loading && tasks.length === 0 && (
              <EmptyList>Nenhuma tarefa em atraso. Tudo em dia!</EmptyList>
            )}
          </TaskRowList>

          {totalPages > 1 && (
            <PaginationControls
              page={page}
              totalPages={totalPages}
              setPage={setPage}
            />
          )}
        </Box>
      </Modal>

      <ConfirmDialog
        open={!!taskToCancel}
        title="Cancelar Tarefa"
        description={`A tarefa "${taskToCancel?.task_title ?? ""}" será apagada de vez. Deseja continuar?`}
        confirmText="Apagar"
        cancelText="Voltar"
        onClose={() => setTaskToCancel(null)}
        onConfirm={handleConfirmCancel}
      />
    </>
  );
};

export default TaskOverdueModal;
